import { Card } from '@/components/ui';
import { ArrowLeft } from 'lucide-react';

/** Card shell matching CargoDetailsView's SectionCard padding at both widths. */
function SectionCardSkeleton({ titleWidth, rows }: { titleWidth: string; rows: number }) {
  return (
    <Card noPadding>
      <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
        {/* Card Title */}
        <div className="flex items-center gap-2 border-b border-gray-200 pb-3 dark:border-gray-700 sm:gap-3 sm:pb-4">
          <div className="h-5 w-5 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className={`h-5 animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${titleWidth}`} />
        </div>

        {/* Card Content Lines */}
        <div className="space-y-3 sm:space-y-4">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex justify-between gap-3">
              <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/**
 * Skeleton loading component for Cargo Details Page
 * Matches the structure of CargoDetailsView, including its phone layout - the
 * two-up key facts and the tighter card padding - so the page does not reflow
 * when the real data lands.
 */
export function CargoDetailsSkeleton() {
  return (
    <div className="space-y-5 sm:space-y-8">
      {/* Header Section Skeleton */}
      <div className="flex flex-col gap-3 sm:gap-6">
        {/* Back Button Skeleton */}
        <div className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4 text-gray-300 dark:text-gray-600" />
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          {/* Title and Info Skeleton */}
          <div className="min-w-0 space-y-1.5 sm:space-y-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-6 sm:w-6" />
              <div className="h-7 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-8 sm:w-48" />
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-1 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-1 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-5 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>

          {/* Action Buttons Skeleton */}
          <div className="flex gap-2 sm:gap-3">
            <div className="h-9 flex-1 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:w-20 sm:flex-none" />
            <div className="h-9 flex-1 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:w-20 sm:flex-none" />
          </div>
        </div>
      </div>

      {/* Key Information Cards Skeleton */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} noPadding>
            <div className="flex flex-col gap-1 p-3 sm:items-center sm:gap-3 sm:p-6">
              <div className="flex items-center gap-2 sm:flex-col sm:gap-3">
                <div className="h-7 w-7 flex-shrink-0 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700 sm:h-12 sm:w-12" />
                <div className="h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-4" />
              </div>
              <div className="h-6 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-8" />
            </div>
          </Card>
        ))}
      </div>

      {/* Main Information Cards Skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <SectionCardSkeleton titleWidth="w-32" rows={5} />
        <SectionCardSkeleton titleWidth="w-40" rows={4} />
        <SectionCardSkeleton titleWidth="w-24" rows={2} />
        <SectionCardSkeleton titleWidth="w-44" rows={4} />
      </div>

      {/* Itinerary Section Skeleton */}
      <Card noPadding>
        <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
          {/* Section Header */}
          <div className="flex items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700 sm:pb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-5 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="h-9 w-24 flex-shrink-0 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* Itinerary Items Skeleton */}
          <div className="space-y-3 sm:space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-dark-800 sm:gap-4 sm:p-4"
              >
                <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-7 w-7 flex-shrink-0 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700 sm:h-8 sm:w-8" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="h-8 w-8 flex-shrink-0 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
